export interface Item {
  id: number;
  name: string;
  checked: boolean;
}

export interface AuthStatus {
  hasAccount: boolean;
  sessionValid: boolean;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (res.status === 204) return undefined as T;
  const body = (await res.json().catch(() => null)) as (T & { error?: string }) | null;
  if (!res.ok) {
    throw new ApiError(res.status, body?.error ?? `Request failed (${res.status})`);
  }
  return body as T;
}

export const api = {
  status(): Promise<AuthStatus> {
    return request<AuthStatus>('/api/auth/status');
  },

  checkSignup(token: string): Promise<{ hasAccount: boolean; tokenValid: boolean }> {
    return request(`/api/auth/signup/options?token=${encodeURIComponent(token)}`);
  },

  signup(token: string, username: string, password: string): Promise<{ ok: boolean }> {
    return request('/api/auth/signup', { method: 'POST', body: JSON.stringify({ token, username, password }) });
  },

  login(username: string, password: string): Promise<{ ok: boolean }> {
    return request('/api/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) });
  },

  logout(): Promise<{ ok: boolean }> {
    return request('/api/auth/logout', { method: 'POST' });
  },

  items(): Promise<{ items: Item[] }> {
    return request('/api/items');
  },

  addItem(name: string): Promise<{ item: Item }> {
    return request('/api/items', { method: 'POST', body: JSON.stringify({ name }) });
  },

  updateItem(id: number, patch: { name?: string; checked?: boolean }): Promise<{ item: Item }> {
    return request(`/api/items/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
  },

  deleteItem(id: number): Promise<void> {
    return request(`/api/items/${id}`, { method: 'DELETE' });
  },

  reorder(orderedIds: number[]): Promise<{ ok: boolean }> {
    return request('/api/items/reorder', { method: 'POST', body: JSON.stringify({ orderedIds }) });
  },

  clearChecked(): Promise<{ removed: number }> {
    return request('/api/clear-checked', { method: 'POST' });
  },
};
